import { createReducer, on } from '@ngrx/store';
import { INVOICE_MAX_SELECTION_ALLOWED } from '../../../constants/default.const';
import { Invoice } from '../../../models/invoice-model';
import {
    clearInvoiceDetailInStore,
    clearInvoicesinStore,
    failLoadInvoiceDetails,
    fetchInvoices,
    loadInvoicesAction,
    setInvoiceDetailData,
    setInvoicesLoading,
    setInvoiceTotalDue,
    setNoMoreRecordInvoices,
    setSelectedInvoices,
    unsetSelectedInvoices
} from './invoices.actions';

export const invoicesFeatureKey = 'invoices';

export interface InvoiceFilters {
    startDate?: string;
    endDate?: string;
    search?: string;
    status?: string;
}

export interface InvoicesState {
    pageNumber: number;
    invoices: Invoice[];
    selectedInvoices: string[];
    loading: boolean;
    noMoreRecord: boolean;
    invoiceDetail: Invoice | null;
    filters: InvoiceFilters;
    invoiceTotalDue?: number;
}

export const initialState: InvoicesState = {
    pageNumber: 1,
    invoices: [],
    selectedInvoices: [],
    loading: false,
    noMoreRecord: false,
    invoiceDetail: null,
    filters: {},
    invoiceTotalDue: undefined
};

export const invoicesReducer = createReducer(
    initialState,
    on(loadInvoicesAction, (state: InvoicesState, { invoices }: { invoices: Invoice[] }) => {
        if (invoices && invoices.length > 0) {
            return {
                ...state,
                invoices: [...state.invoices, ...invoices],
                pageNumber: state.pageNumber + 1
            };
        }
        return state;
    }),
    on(setInvoicesLoading, (state: InvoicesState, { loading }: { loading: boolean }) => {
        return { ...state, loading };
    }),
    on(setNoMoreRecordInvoices, (state: InvoicesState, { record }: { record: boolean }) => {
        return { ...state, noMoreRecord: record };
    }),
    on(clearInvoicesinStore, (state: InvoicesState) => {
        return { ...state, invoices: [], pageNumber: 1, selectedInvoices: [] };
    }),
    // ADO-116163: persist the active filters so a store-driven refresh (e.g. the post-payment
    // invoice-list refresh in payment.effects) can re-fetch with the same filters the user set,
    // instead of silently resetting the list to unfiltered. clearInvoicesinStore deliberately
    // leaves these untouched so they survive the clear-then-refetch sequence.
    on(fetchInvoices, (state: InvoicesState, { startDate, endDate, search, status }) => {
        return { ...state, filters: { startDate, endDate, search, status } };
    }),
    on(setSelectedInvoices, (state: InvoicesState, { selectedInvoices }: { selectedInvoices: string[] }) => {
        if (selectedInvoices.length <= INVOICE_MAX_SELECTION_ALLOWED) {
            return { ...state, selectedInvoices };
        }

        // Reject the update entirely when it would exceed the cap
        return state;
    }),
    on(unsetSelectedInvoices, (state: InvoicesState, { unselectInvoices }) => ({
        ...state,
        selectedInvoices: state.selectedInvoices.filter(invoiceId => !unselectInvoices.includes(invoiceId))
    })),
    on(failLoadInvoiceDetails, (state: InvoicesState, { error }) => {
        return { ...state, invoiceDetail: null, error };
    }),
    on(setInvoiceDetailData, (state: InvoicesState, { data }) => {
        return { ...state, invoiceDetail: data };
    }),
    on(clearInvoiceDetailInStore, (state: InvoicesState) => {
        return { ...state, invoiceDetail: null };
    }),
    on(setInvoiceTotalDue, (state: InvoicesState, { totalDue }) => {
        return { ...state, invoiceTotalDue: totalDue };
    })
);
